import {
  PROTOCOL_VERSION,
  isAutopicMessage,
  loaderToIframeMessage,
  type EmbedMode,
  type IframeToLoaderMessage,
  type LoaderToIframeMessage,
} from "@autopic/shared";

/**
 * The iframe side of the loader <-> iframe protocol. Only meaningful when the
 * app is rendered inside another window (the embed). When standalone
 * (window === window.parent) every send is a no-op.
 */

const inIframe = typeof window !== "undefined" && window.parent !== window;

function post(message: IframeToLoaderMessage): void {
  if (!inIframe) return;
  // We post to '*': the loader validates the message origin on its side, and
  // the payload carries no secrets. This keeps the loader origin-agnostic.
  try {
    window.parent.postMessage(message, "*");
  } catch {
    /* noop */
  }
}

export function sendReady(): void {
  post({ type: "autopic:ready", v: PROTOCOL_VERSION });
}

export function sendResize(height: number): void {
  post({
    type: "autopic:resize",
    v: PROTOCOL_VERSION,
    height: Math.ceil(height),
  });
}

export function sendBookingComplete(reference: string, garageId: string): void {
  post({
    type: "autopic:booking-complete",
    v: PROTOCOL_VERSION,
    reference,
    garageId,
  });
}

export function sendClosePopup(): void {
  post({ type: "autopic:close-popup", v: PROTOCOL_VERSION });
}

export function sendTyreOrderComplete(
  reference: string,
  garageId: string,
  environment: "test" | "demo" = "test",
): void {
  post({
    type: "autopic:tyre-order-complete",
    v: PROTOCOL_VERSION,
    reference,
    garageId,
    environment,
  });
}

export function isEmbedded(): boolean {
  return inIframe;
}

/**
 * Observe the document height and forward resize messages to the loader so the
 * iframe grows/shrinks with content. Returns a disconnect fn.
 */
export function startResizeReporter(el: HTMLElement): () => void {
  if (!inIframe) return () => {};

  let last = -1;
  const report = () => {
    // Use scrollHeight of the measured element + a hairline to avoid clipping.
    const height = Math.ceil(el.getBoundingClientRect().height);
    if (height !== last && height > 0) {
      last = height;
      sendResize(height);
    }
  };

  const ro = new ResizeObserver(() => report());
  ro.observe(el);
  // Initial + a deferred pass once fonts/layout settle.
  report();
  const t = window.setTimeout(report, 60);

  return () => {
    ro.disconnect();
    window.clearTimeout(t);
  };
}

/**
 * Listen for loader -> iframe init messages. Resolves the embed mode once the
 * loader announces it. We validate the schema with the shared zod parser.
 */
export function onInit(cb: (msg: LoaderToIframeMessage) => void): () => void {
  if (!inIframe) return () => {};
  const handler = (e: MessageEvent) => {
    if (!isAutopicMessage(e.data)) return;
    const parsed = loaderToIframeMessage.safeParse(e.data);
    if (parsed.success) cb(parsed.data);
  };
  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}

/** Read the embed mode hint from the URL (?mode=inline|popup). */
export function readModeFromUrl(): EmbedMode {
  if (typeof window === "undefined") return "inline";
  const m = new URLSearchParams(window.location.search).get("mode");
  return m === "popup" ? "popup" : "inline";
}
